import { MODES } from "#src/constants/mode.ts";
import { currencyFormatter, isBoolean } from "#src/util/helpers.js";
import { type Product, type ProductTypeInfo } from "#src/types/Product.ts";
import { PRODUCTS } from "#src/constants/products.ts";
import {
  type GuaranteedIssueDataItem,
  type ModeDataItem,
  type ParamSuggestion,
  type QuoteParams,
  type QuoteRequestParams,
  type RenderRules,
  insuredQuoteData,
} from "#src/types/QuoteParams.ts";

export const FINAL_EXPENSE_MAX_AGE = 85;
export const FINAL_EXPENSE_MIN_AGE = 45;
export const FINAL_EXPENSE_MAX_FACE_AMOUNT = 50000;
const FORMATTED_FINAL_EXPENSE_MAX_FACE_AMOUNT = currencyFormatter(
  FINAL_EXPENSE_MAX_FACE_AMOUNT,
  0
);

export interface FinalExpenseProductTypeInfo extends ProductTypeInfo {
  isFinalExpense: boolean;
  guaranteedIssueOptions: GuaranteedIssueDataItem[];
  giveParamsSuggestion?: ParamSuggestion;
}

function showHealth(conversion: boolean) {
  return !conversion;
}
export default function FinalExpense(): FinalExpenseProductTypeInfo {
  return {
    get type(): Product {
      return PRODUCTS.FINAL_EXPENSE;
    },
    get isFinalExpense(): boolean {
      return true;
    },
    renderRules(
      _quoteParams,
      options = { conversion: false }
    ): Partial<RenderRules> {
      return {
        saveAge: true,
        faceAmount: true,
        insuredHealth: showHealth(options.conversion),
        guaranteedIssue: true,
        mode: true,
        state: true,
      };
    },
    get display(): string {
      return "Final Expense";
    },
    get defaults(): Partial<QuoteRequestParams> {
      return {
        categories: [this.requestCategory],
        save_age: false,
        guaranteed_issue: null,
      };
    },
    get requestCategory(): string {
      return "whole_life.final_expense";
    },
    get guaranteedIssueOptions(): GuaranteedIssueDataItem[] {
      return [
        {
          text: "Yes",
          value: true,
        },
        {
          text: "No",
          value: null,
        },
      ];
    },
    get modes(): ModeDataItem[] {
      return MODES;
    },
    eAppParser(model): Partial<QuoteParams> {
      let face_amount = model?.face_amount;
      if (
        !model?.face_amount ||
        model?.face_amount > FINAL_EXPENSE_MAX_FACE_AMOUNT ||
        model?.face_amount < 0
      )
        face_amount = 25000;

      let save_age = model.save_age;
      if (!isBoolean(save_age)) save_age = this.defaults.save_age;

      let guaranteed_issue = model.guaranteed_issue;
      if (
        !this.guaranteedIssueOptions?.some(v => v.value === guaranteed_issue)
      ) {
        guaranteed_issue = this.defaults.guaranteed_issue;
      }

      return {
        face_amount,
        save_age,
        guaranteed_issue,
      };
    },
    giveParamsSuggestion(age, params): string {
      if (age < FINAL_EXPENSE_MIN_AGE) {
        return `The minimum age for Final Expense is ${FINAL_EXPENSE_MIN_AGE}`;
      } else if (
        params.face_amount &&
        params.face_amount > FINAL_EXPENSE_MAX_FACE_AMOUNT
      ) {
        return `The max death benefit for Final Expense is ${FORMATTED_FINAL_EXPENSE_MAX_FACE_AMOUNT}`;
      }
      return "";
    },
    toQuoteRequest(args): Partial<QuoteRequestParams> {
      let category = this.requestCategory;
      if (args.params.guaranteed_issue) {
        category = `${this.requestCategory}.guaranteed_issue`;
      }

      return {
        agent: { id: args.agentId },
        compare: false,

        ...insuredQuoteData(args.insured),

        selected_type: args.params.selected_type,
        state: args.params.state,
        mode: args.params.mode,
        categories: [category],

        save_age: args.params.save_age,
        face_amount: args.params.face_amount,
        guaranteed_issue: args.params.guaranteed_issue,
      };
    },
  };
}
